import { PricingType, Promotion, Currency } from '@css-xp/graphtypes/src/generated-types-new';
import { OrderCreationItemDto } from '@atlassian/commerce-ccp-gateway-client';
import type { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { PARTNER_DISCOUNT_TYPE, STATSIG_FEATURE_GATES, STATUSPAGE_PRODUCT_ID } from '@css-xp/ppc-constants';
import { v4 as generateUuid } from 'uuid';
import {
  useOrderEstimatePDFDownload,
  useEstimateJsonDownload,
  useProcessLineItemsForOrderIntentEstimate,
  PartnerQuoteLineItems,
  PartnerQuoteLineItem,
} from '@css-xp/ppc-checkout';
import { getChargeElement } from '@css-xp/base/entitlement-utils';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { AnalyticsTrackAction, AnalyticsTrackSubject, sendAnalyticsEvent } from '@atlassian/temp-analytics';
import { PricingPlanType } from '@atlassian/cssxp-ccp-types';
import { getLinkedEntitlementChargeElement, isStatusPageAddOnProduct } from '@css-xp/ppc-utils';
import { getPartnerPromotions } from '../store/utils/get-partner-promotions';
import { usePartnerPromotionsPayload, useStore } from '../store';
import { State, OfferingAndPricingPlan } from '../store/types';
import { PresentationLayerProducts, Product, ProductTypeEnum } from '../ui/product-line-items/src/types';
import { ProductStatus, CartLinkedEntitlementsSchema, PromotionTypes, CartProductSchema } from '../types';
import { getProductIdsOfferingId, getSelectedAppsData, isNewEntitlement, stitchChangeKeyObjects } from '../store/utils';
import { getPhasedPromotionPayload } from '../store/utils/get-bill-estimate';
import { getAdjustedDateForMonthEstimate } from '../utils';
import { getPricingPlanId } from '../store/utils/get-pricing-plan-id';

type ProductLineItems = {
  cloudSite: string;
  products: PresentationLayerProducts;
}[];

type DownloadEstimatePDF = {
  invoiceGroupId: string;
  transactionAccountId: string;
  items: ProductLineItems;
  isPartnerMarginIncluded: boolean;
};

type TransformLineItemsParams = {
  productLineItems: ProductLineItems;
  offeringsAndpricingPlans: State['offeringsAndpricingPlans'];
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  selectedProducts: State['selectedProducts'];
  storeProducts: State['products'];
  entitlementIdChangedBillDateMap: State['nextBillDate']['entitlementLevel'];
  shouldExcludeDiscretionaryDiscount?: boolean;
  pricingEligibility?: CCPPricing;
  currency?: Currency;
  isNonCommercialPricingEnabled?: boolean;
  isExcludeExpiredPromotionEnabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
};

export const filterNotRequiredPromotions = (
  promotions: Promotion[],
  removedPromotionsInstanceId: (string | null | undefined)[],
  shouldExcludeDiscretionaryDiscount?: boolean,
  isExcludeExpiredPromotionEnabled?: boolean
) => {
  const excludePromotionTypes = [PARTNER_DISCOUNT_TYPE];

  if (shouldExcludeDiscretionaryDiscount) {
    excludePromotionTypes.push(PromotionTypes.DISCRETIONARY_DISCOUNT);
  }

  const filteredPromotions = promotions?.filter((promotion: Promotion) => {
    return (
      promotion?.type &&
      !excludePromotionTypes.includes(promotion?.type) &&
      !removedPromotionsInstanceId?.includes(promotion?.promotionInstanceId) &&
      (isExcludeExpiredPromotionEnabled && promotion?.status ? promotion.status.toLowerCase() === 'active' : true)
    );
  });

  return filteredPromotions;
};

const getShowStartDate = (product: Product, storeProducts: State['products']) => {
  /**
   * showStartDate is true when
   * 1. Edition is not changed
   * 2. Product is not new purchase.
   */

  let isEditionChanged = false;
  const { productStatuses } = product;
  const productInitialOfferingId = storeProducts[product.id]?.offeringId;
  if (productStatuses && productStatuses.length > 0) {
    // Checking for edition change
    isEditionChanged =
      productStatuses.includes(ProductStatus.EDITED) && productInitialOfferingId !== product.offeringId;
  }

  return !isNewEntitlement(product) && !isEditionChanged;
};

const getSubscriptionStartTime = (
  entitlementId: string,
  entitlementIdChangedBillDateMap: State['nextBillDate']['entitlementLevel'],
  nextBillDate?: string
) => {
  const _nextBillDate = Number(nextBillDate);
  /**
   * If the bill date is changed for the entitlement, then return the new bill date for all cases.
   *
   * If the bill date is not changed for the entitlement, but the current date is 29th, 30th or 31st of any month,
   * and the next bill date is not present which is in the case of NEW entitlements,
   * then set the subscription start time to the first day of next month and the next billing anchor timestamp to the first day of the second next month.
   *
   * If the bill date is not changed for the entitlement and the current date is not 29th, 30th or 31st of any month,
   * then return next Bill date present in subscription as subscriptionStartTime.
   */

  const {
    isLastThreeDaysOfMonth,
    firstDayOfNextMonthTimeStamp,
    firstDayOfSecondNextMonthTimeStamp,
  } = getAdjustedDateForMonthEstimate();

  if (entitlementIdChangedBillDateMap && entitlementIdChangedBillDateMap[entitlementId]) {
    return {
      subscriptionStartTime: Number(entitlementIdChangedBillDateMap[entitlementId]),
    };
  }

  if (isLastThreeDaysOfMonth && !nextBillDate) {
    return {
      subscriptionStartTime: firstDayOfNextMonthTimeStamp,
      nextBillingAnchorTimestamp: firstDayOfSecondNextMonthTimeStamp,
    };
  }
  // in case any case comes where nextBillDate is any old date, e.g. a free entitlement previously was in paid plan we will skip sending this
  if (nextBillDate && _nextBillDate >= +new Date()) {
    // Check if the nextBillDate is last three days of month, then set the subscriptionStartTime to first day of next month
    // Send actual nextBillDate as well in case of last three days of month to show the actual next bill date in PDF
    const { isLastThreeDaysOfMonth, firstDayOfNextMonthTimeStamp } = getAdjustedDateForMonthEstimate(_nextBillDate);

    if (isLastThreeDaysOfMonth) {
      return {
        subscriptionStartTime: firstDayOfNextMonthTimeStamp,
        actualNextBillDate: _nextBillDate,
      };
    }

    // If the nextBillDate is not last three days of month, then return the nextBillDate as subscriptionStartTime
    return {
      subscriptionStartTime: _nextBillDate,
    };
  }

  return {};
};
const transformAppLineItems = ({
  linkedEntitlements,
  selectedLinkedEntitlements,
  parentProductEntitlementId,
  entitlementIdChangedBillDateMap,
  shouldExcludeDiscretionaryDiscount,
  offeringsAndPricingPlans,
  pricingEligibility,
  currency,
  isNonCommercialPricingEnabled,
  isExcludeExpiredPromotionEnabled,
  isAppEditionSupportEnabled,
}: {
  linkedEntitlements: any[];
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  parentProductEntitlementId: string;
  entitlementIdChangedBillDateMap: State['nextBillDate']['entitlementLevel'];
  shouldExcludeDiscretionaryDiscount?: boolean;
  offeringsAndPricingPlans?: State['offeringsAndpricingPlans'];
  pricingEligibility?: string;
  currency?: Currency;
  isNonCommercialPricingEnabled?: boolean;
  isExcludeExpiredPromotionEnabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
}) =>
  // eslint-disable-next-line max-params
  {
    const appLineItems: any[] = [];
    linkedEntitlements?.forEach((app: any) => {
      const selectedApp = selectedLinkedEntitlements?.[app.id];
      const appsNewlyAppliedPromotions = selectedLinkedEntitlements?.[app.id]?.promotions ?? [];
      let allAppliedPromotions = app?.promotions;
      if (appsNewlyAppliedPromotions?.length) {
        allAppliedPromotions = [...app?.promotions, ...appsNewlyAppliedPromotions];
      }

      // getting promotions to be removed
      const removedPromotions = selectedLinkedEntitlements?.[app.id]?.removePromotions ?? [];
      // getting promotion instance id to be removed
      const removedPromotionsInstanceId = removedPromotions?.map((promotion) => promotion?.promotionInstanceId);

      // filtering out partner discount and other non required promotions
      const filteredPromotions = filterNotRequiredPromotions(
        allAppliedPromotions,
        removedPromotionsInstanceId,
        shouldExcludeDiscretionaryDiscount,
        isExcludeExpiredPromotionEnabled
      );

      let pricingPlanId: string | undefined;
      if (offeringsAndPricingPlans && pricingEligibility) {
        const _pricingPlanId = getPricingPlanId({
          offerings: offeringsAndPricingPlans[app.productId],
          offeringId: selectedApp?.offeringId || app.offeringId,
          currency: currency,
          pricingEligibility: pricingEligibility as `${PricingPlanType}`,
          defaultPricingEligibilityToCommercial: isNonCommercialPricingEnabled,
          enableRetainPricingPlan: !!isNonCommercialPricingEnabled,
          initialPricingPlanId: app.pricingPlanId,
          initialOfferingId: app.offeringId,
          pricingPlanType: app.pricingPlanType,
          entitlementPriceEligibility: app.priceEligibility,
        });
        pricingPlanId = _pricingPlanId || app.pricingPlanId;
      }

      const appOfferingId = isAppEditionSupportEnabled ? selectedApp?.offeringId ?? app?.offeringId : app?.offeringId;
      /**
       * we are converting productId = offeringId for statuspage addons and saving in store.
       * we are making this change because all statuspage addons have same product id
       * while placing an order again we revert productId with STATUSPAGE_PRODUCT_ID
       * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
       */
      const productId = isStatusPageAddOnProduct(app.productId) ? STATUSPAGE_PRODUCT_ID : app.productId;
      const appLineItem = {
        id: app.id,
        productKey: productId,
        cloudSite: app.cloudSite,
        entitlementNumber: app.entitlementNumber,
        orgName: app.organisationName,
        status: 'ACTIVE',
        name: app.name,
        offeringName: app.offeringName,
        pricingType: 'PAID',
        pricingPlanKey: pricingPlanId || app.pricingPlanId,
        trialLengthInDays: app.trialLengthInDays,
        logoId: app.logoId,
        offeringId: appOfferingId,
        estimatePrice: 0,
        lineItemId: app.id,
        estimatedNumberOfUsers: app.activeUsers,
        chargeQuantities: [{ chargeElement: getLinkedEntitlementChargeElement(productId), quantity: app.activeUsers }],
        billingCycleLabel: app.billingCycle,
        promotions: filteredPromotions ?? [],
        ...getSubscriptionStartTime(parentProductEntitlementId, entitlementIdChangedBillDateMap, app?.nextBillDate),
        showStartDate: !isNewEntitlement(app),
        hideBillingPeriod: app.offeringName !== 'Free',
        relationshipType: app?.relationshipType,
      };
      appLineItems.push(appLineItem);
    });
    return appLineItems;
  };

export const transformLineItems = ({
  productLineItems,
  offeringsAndpricingPlans,
  selectedLinkedEntitlements,
  selectedProducts,
  storeProducts,
  entitlementIdChangedBillDateMap,
  shouldExcludeDiscretionaryDiscount,
  pricingEligibility,
  currency,
  isNonCommercialPricingEnabled,
  isExcludeExpiredPromotionEnabled,
  isAppEditionSupportEnabled,
}: TransformLineItemsParams) => {
  const lineItems: PartnerQuoteLineItems = [];
  productLineItems?.forEach((productLineItem) => {
    productLineItem.products.forEach((product) => {
      if (product.type === ProductTypeEnum.CONTAINER) {
        const totalJiraProductInContainer: number = product.products.length;
        product.products.forEach((containerProduct, index) => {
          const isLastProductInContainer: boolean = index === totalJiraProductInContainer - 1;

          let productOffering: OfferingAndPricingPlan | undefined;
          let pricingPlanId: string | undefined;
          if (offeringsAndpricingPlans) {
            productOffering = offeringsAndpricingPlans[containerProduct.productId].find(
              (productOffering) => productOffering.id === containerProduct.offeringId
            );
            if (pricingEligibility) {
              const initialContainerProduct = storeProducts[containerProduct.id];
              const _pricingPlanId = getPricingPlanId({
                offerings: offeringsAndpricingPlans[containerProduct.productId],
                offeringId: containerProduct.offeringId,
                currency: currency,
                pricingEligibility: pricingEligibility as `${PricingPlanType}`,
                defaultPricingEligibilityToCommercial: true,
                enableRetainPricingPlan: true,
                initialPricingPlanId: initialContainerProduct.pricingPlanId,
                initialOfferingId: initialContainerProduct.offeringId,
                pricingPlanType: containerProduct.pricingPlanType,
                entitlementPriceEligibility: initialContainerProduct.priceEligibility,
              });
              pricingPlanId = _pricingPlanId || containerProduct.pricingPlanId;
            }
          }
          /**
           * pricingType Free and Limited Free are considered as free products.
           * pricingType Free is for subscription with Plan `Free` and `Beta`.
           * pricingType Limited Free is for subscription with Plan `Free Standard`.
           */
          const isFreeProduct =
            productOffering?.pricingType === PricingType.FREE ||
            productOffering?.pricingType === PricingType.LIMITED_FREE;

          // getting promotions to be removed
          const removedPromotions = selectedProducts?.[containerProduct.id]?.removePromotions ?? [];
          // getting promotion instance id to be removed
          const removedPromotionsInstanceId = removedPromotions?.map((promotion) => promotion?.promotionInstanceId);

          // filtering out partner discount and other non required promotions
          const filteredPromotions = filterNotRequiredPromotions(
            containerProduct?.promotions,
            removedPromotionsInstanceId,
            shouldExcludeDiscretionaryDiscount,
            isExcludeExpiredPromotionEnabled
          );

          const lineItem: PartnerQuoteLineItem = {
            lineItemId: containerProduct?.itemId || containerProduct.id,
            id: containerProduct.id,
            name: containerProduct.name,
            cloudSite: containerProduct?.cloudSite,
            entitlementNumber: containerProduct?.entitlementNumber,
            orgName: containerProduct.organisationName,
            logoId: containerProduct.productId,
            productKey: containerProduct.productId,
            appLineItems: isLastProductInContainer
              ? transformAppLineItems({
                  linkedEntitlements: product.linkedEntitlements,
                  selectedLinkedEntitlements,
                  parentProductEntitlementId: containerProduct.id,
                  entitlementIdChangedBillDateMap,
                  shouldExcludeDiscretionaryDiscount,
                  offeringsAndPricingPlans: offeringsAndpricingPlans,
                  pricingEligibility,
                  isNonCommercialPricingEnabled,
                  currency,
                  isExcludeExpiredPromotionEnabled,
                  isAppEditionSupportEnabled,
                })
              : [],
            estimatedPrice: containerProduct.listPrice,
            estimatedNumberOfUsers: Number(containerProduct.activeUsers),
            billingCycleLabel: containerProduct.billingCycle,
            pricingPlanId: pricingPlanId,
            pricingPlanType: pricingEligibility || CCPPricing.COMMERCIAL,
            pricingPlanLabel: '',
            offeringKey: containerProduct.offeringId,
            offeringName: productOffering?.offeringName || '',
            userTierLabel: '',
            userTierName: '',
            chargeQuantities: [
              {
                chargeElement: getChargeElement(containerProduct.productId),
                quantity: Number(containerProduct.activeUsers),
              },
            ],
            promotions: filteredPromotions ?? [],
            totalPriceOfApps: 0,
            isItAJiraProduct: true,
            isFreeProduct,
            isApp: false,
            ...getSubscriptionStartTime(
              containerProduct.id,
              entitlementIdChangedBillDateMap,
              containerProduct.nextBillDate
            ),
            showStartDate: getShowStartDate(containerProduct, storeProducts),
            hideBillingPeriod: !isFreeProduct,
          };
          lineItems.push(lineItem);
        });
      }
      if (product.type === ProductTypeEnum.STANDALONE) {
        let productOffering: OfferingAndPricingPlan | undefined;
        let pricingPlanId: string | undefined;
        if (offeringsAndpricingPlans) {
          productOffering = offeringsAndpricingPlans[product.productId].find(
            (productOffering) => productOffering.id === product.offeringId
          );
          if (pricingEligibility) {
            const initialProduct = storeProducts[product.id];
            const _pricingPlanId = getPricingPlanId({
              offerings: offeringsAndpricingPlans[product.productId],
              offeringId: product.offeringId,
              currency: currency,
              pricingEligibility: pricingEligibility as `${PricingPlanType}`,
              defaultPricingEligibilityToCommercial: true,
              enableRetainPricingPlan: true,
              initialPricingPlanId: initialProduct.pricingPlanId,
              initialOfferingId: initialProduct.offeringId,
              pricingPlanType: product.pricingPlanType,
              entitlementPriceEligibility: initialProduct.priceEligibility,
            });
            pricingPlanId = _pricingPlanId || product.pricingPlanId;
          }
        }

        /**
         * pricingType Free and Limited Free are considered as free products.
         * pricingType Free is for subscription with Plan `Free` and `Beta`.
         * pricingType Limited Free is for subscription with Plan `Free Standard`.
         */
        const isFreeProduct =
          productOffering?.pricingType === PricingType.FREE ||
          productOffering?.pricingType === PricingType.LIMITED_FREE;

        // getting promotions to be removed
        const removedPromotions = selectedProducts?.[product.id]?.removePromotions ?? [];
        // getting promotion instance id to be removed
        const removedPromotionsInstanceId = removedPromotions?.map((promotion) => promotion?.promotionInstanceId);

        // filtering out partner discount and other non required promotions
        const filteredPromotions = filterNotRequiredPromotions(
          product?.promotions,
          removedPromotionsInstanceId,
          shouldExcludeDiscretionaryDiscount
        );

        const lineItem: PartnerQuoteLineItem = {
          lineItemId: product?.itemId || product.id,
          id: product.id,
          name: product.name,
          cloudSite: product?.cloudSite,
          entitlementNumber: product.entitlementNumber,
          orgName: product.organisationName,
          billingCycleLabel: product.billingCycle,
          productKey: product.productId,
          pricingPlanId: pricingPlanId || product.pricingPlanId,
          appLineItems: transformAppLineItems({
            linkedEntitlements: product.linkedEntitlements,
            selectedLinkedEntitlements,
            parentProductEntitlementId: product.id,
            entitlementIdChangedBillDateMap,
            shouldExcludeDiscretionaryDiscount,
            pricingEligibility,
            offeringsAndPricingPlans: offeringsAndpricingPlans,
            currency,
            isNonCommercialPricingEnabled,
            isAppEditionSupportEnabled,
          }),
          estimatedPrice: product.listPrice,
          estimatedNumberOfUsers: Number(product.activeUsers),
          logoId: product.productId,
          pricingPlanType: pricingEligibility || CCPPricing.COMMERCIAL,
          pricingPlanLabel: '',
          offeringKey: product.offeringId,
          offeringName: productOffering?.offeringName || '',
          userTierLabel: '',
          userTierName: '',
          chargeQuantities: [
            {
              chargeElement: getChargeElement(product.productId),
              quantity: Number(product.activeUsers),
            },
          ],
          promotions: filteredPromotions ?? [],
          totalPriceOfApps: 0,
          isItAJiraProduct: false,
          isFreeProduct,
          isApp: false,
          ...getSubscriptionStartTime(product.id, entitlementIdChangedBillDateMap, product.nextBillDate),
          showStartDate: getShowStartDate(product, storeProducts),
          hideBillingPeriod: !isFreeProduct,
        };
        lineItems.push(lineItem);
      }
    });
  });
  return lineItems;
};
export const handlePhasedPartnerPromotions = ({
  initialLineItems,
  lineItems,
  partnerPromotions,
  partnerPromotionBenefits,
  selectedProducts,
  products,
  linkedEntitlements,
}: {
  initialLineItems: PartnerQuoteLineItem[];
  lineItems: OrderCreationItemDto[];
  partnerPromotions: State['partnerPromotions'];
  partnerPromotionBenefits: State['partnerPromotionBenefits'];
  selectedProducts: State['selectedProducts'];
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  products: State['products'];
  linkedEntitlements: State['linkedEntitlements'];
}) => {
  const cartProducts = Object.values(stitchChangeKeyObjects(products, selectedProducts));
  const productOrContainerIds = cartProducts.map(({ id, containerId }) => containerId || id);
  const cartApps: CartLinkedEntitlementsSchema[] = [];
  productOrContainerIds?.forEach((productOrContainerId) => {
    linkedEntitlements[productOrContainerId]?.forEach((app) => cartApps.push(app));
  });

  const lineItemIdToLineItemMap = initialLineItems.reduce((acc, lineItem: PartnerQuoteLineItem) => {
    acc[lineItem.lineItemId] = lineItem;
    lineItem.appLineItems.forEach((appLineItem) => {
      acc[appLineItem.lineItemId] = appLineItem;
    });
    return acc;
  }, {} as Record<string, PartnerQuoteLineItem>);

  lineItems.forEach((lineItem) => {
    // @ts-ignore
    lineItem.promotions = getPhasedPromotionPayload({
      // @ts-ignore
      lineItem: {
        ...lineItem,
        productId: lineItemIdToLineItemMap[lineItem.itemId].productKey,
        orderType: lineItem.type as OrderItemType,
        entitlementId: lineItemIdToLineItemMap[lineItem.itemId].id,
      },
      lineItemPromotionKey: 'promotions',
      partnerPromotions,
      partnerPromotionBenefits,
      cartProducts,
      cartApps,
    });
  });
};
export const useEstimateDownload = () => {
  const { isEstimatePDFDownloading, downloadOrderEstimatePDF } = useOrderEstimatePDFDownload();
  const { isEstimateJsonIsDownloading, prepareAndSaveJson } = useEstimateJsonDownload();
  const { processLineItemsForOrderEstimate } = useProcessLineItemsForOrderIntentEstimate({
    lineItemsData: [],
  });
  const [state, actions] = useStore();
  const {
    offeringsAndpricingPlans,
    selectedLinkedEntitlements,
    selectedProducts,
    nextBillDate,
    products,
    linkedEntitlements,
    billingProfiles,
    featureFlags,
  } = state;
  const partnerPromotionsPayload = usePartnerPromotionsPayload();
  const isNonCommercialPricingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING];
  const isMultiCurrencyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] ?? false;
  const isExcludeExpiredPromotionEnabled =
    featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_EXCLUDE_EXPIRED_PROMOTIONS_IN_UOF] ?? false;

  const isAddOnsSearchEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] ?? false;
  const isAppEditionSupportEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT] ?? false;

  const prepareLineItems = async (requestPayload: DownloadEstimatePDF) => {
    const currentPricingEligibility = billingProfiles?.find(
      (billingProfile) => billingProfile.id === requestPayload.invoiceGroupId
    )?.shipToPriceEligibility;
    const currentCurrency =
      billingProfiles?.find((billingProfile) => billingProfile.id === requestPayload.invoiceGroupId)?.currency ||
      Currency.USD;
    // fetching partner promotions and benefits
    const partnerPromotionPromise = requestPayload.isPartnerMarginIncluded
      ? await getPartnerPromotions({
          ...partnerPromotionsPayload,
          isExpandPromotionBenefits: true,
        })
      : Promise.resolve({
          productIdPartnerPromotionIdMap: undefined,
          promotionIdToBenefitsMap: undefined,
        });
    // fetching all the apps and products product ids to fetch the offering data for pricing eligibility
    const selectedAppsArr = getSelectedAppsData(state);
    const selectedAppProductIdArr = selectedAppsArr?.map((selectedApp) => selectedApp.productId) ?? [];
    const selectedProductsArr = Object.values(
      stitchChangeKeyObjects<CartProductSchema>(state.products, state.selectedProducts)
    );
    const selectedProductsIds = selectedProductsArr.map((product) => product.productId);
    const productOfferingPromise = getProductIdsOfferingId({
      existingOfferingsAndPricingPlans: offeringsAndpricingPlans,
      productIds: [...selectedProductsIds, ...selectedAppProductIdArr],
      isMultiCurrencyEnabled,
      isAddOnsSearchEnabled,
    });

    const [
      { productIdPartnerPromotionIdMap = {}, promotionIdToBenefitsMap = {} },
      offeringsAndPricingPlansUpdated,
    ] = await Promise.all([partnerPromotionPromise, productOfferingPromise]);
    // updating store with latest product promotions and offering and pricing plans
    actions.updatePartnerPromotions(productIdPartnerPromotionIdMap, promotionIdToBenefitsMap);
    actions.updateOfferingAndPricingPlans(offeringsAndPricingPlansUpdated);

    const lineItems = transformLineItems({
      productLineItems: requestPayload.items,
      offeringsAndpricingPlans: offeringsAndPricingPlansUpdated,
      selectedLinkedEntitlements: selectedLinkedEntitlements,
      selectedProducts: selectedProducts,
      shouldExcludeDiscretionaryDiscount: true,
      entitlementIdChangedBillDateMap: nextBillDate.entitlementLevel,
      storeProducts: products,
      pricingEligibility: currentPricingEligibility,
      // FIXME :: Redefine the currency type same at all places
      // @ts-ignore
      currency: currentCurrency,
      isNonCommercialPricingEnabled: isNonCommercialPricingEnabled,
      isExcludeExpiredPromotionEnabled,
      isAppEditionSupportEnabled,
    });

    const processedLineItems = processLineItemsForOrderEstimate(lineItems, productIdPartnerPromotionIdMap);

    if (productIdPartnerPromotionIdMap && promotionIdToBenefitsMap) {
      handlePhasedPartnerPromotions({
        lineItems: processedLineItems,
        initialLineItems: lineItems,
        partnerPromotions: productIdPartnerPromotionIdMap,
        partnerPromotionBenefits: promotionIdToBenefitsMap,
        selectedLinkedEntitlements,
        selectedProducts,
        products,
        linkedEntitlements,
      });
    }

    return processedLineItems;
  };

  const downloadEstimatePDF = async (requestPayload: DownloadEstimatePDF) => {
    const processedLineItems = await prepareLineItems(requestPayload);
    const isPDFDownloaded = await downloadOrderEstimatePDF({
      invoiceGroupId: requestPayload.invoiceGroupId,
      transactionAccountId: requestPayload.transactionAccountId,
      items: processedLineItems,
      orderIntentId: generateUuid(),
    });

    isPDFDownloaded && sendAnalyticsEvent(AnalyticsTrackAction.DOWNLOADED, AnalyticsTrackSubject.ORDER_PDF);
  };

  const prepareAndSaveEstimateJSON = async (requestPayload: DownloadEstimatePDF) => {
    const processedLineItems = await prepareLineItems(requestPayload);
    await prepareAndSaveJson({
      intentEstimateRequestPayload: {
        invoiceGroupId: requestPayload.invoiceGroupId,
        transactionAccountId: requestPayload.transactionAccountId,
        items: processedLineItems,
        orderIntentId: generateUuid(),
      },
      onDownloadComplete: () => {
        sendAnalyticsEvent(AnalyticsTrackAction.DOWNLOADED, AnalyticsTrackSubject.ORDER_JSON);
      },
    });
  };

  return {
    downloadEstimatePDF,
    isEstimatePDFDownloading,
    prepareAndSaveEstimateJSON,
    isEstimateJsonIsDownloading,
  };
};
